import { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormModal, ConfirmationModal } from "@/components";
import { useIntegration, useSettings } from "@/hooks";
import { CreateSellerToken, SellerTokens } from "@/models";
import { copyTextToClipboard, showToast } from "@/utils";
import { IntegrationEmptyState } from "@/pages/configuracoes/integracao/_EmptyState";
import { TokenItem } from "./_token-item";
import { Button, Checkbox, Input, Select } from "barte-design-system";
import { DuplicateIcon } from "@/styles/icons";
const tokenCreateScheama = yup.object().shape({
  title: yup.string().required("Preenchimento obrigatório!"),
  expiration: yup
    .string()
    .oneOf(["NO_EXPIRATION", "THIRTY_DAYS", "SIXTY_DAYS"])
    .required("Preenchimento obrigatório!"),
});
const expirationOptions = [
  { label: "Sem expiração", value: "NO_EXPIRATION" },
  { label: "30 dias", value: "THIRTY_DAYS" },
  { label: "60 dias", value: "SIXTY_DAYS" },
];

export const ApiKeys = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<SellerTokens | null>(null);
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const { postSellerApiToken } = useSettings();
  const {
    tokens,
    isAlertActiveOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    setIsAlertActiveOpen,
    deleteToken,
    getTokensInitialData,
    changeActive,
    chooseToken,
    isLoading,
    setIsLoading,
  } = useIntegration();

  useEffect(() => {
    getTokensInitialData();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(tokenCreateScheama),
  });
  const getTitleValue = watch("title");
  const getExpirationValue = watch("expiration");
  const closeCreateModal = () => {
    if (createdToken && !copyConfirmed) {
      showToast({
        message:
          "Para fechar, confirme que copiou o token e o guardou em um local seguro.",
        type: "error",
      });
      return;
    }
    setIsCreateModalOpen(false);
    setCreatedToken(null);
    setCopyConfirmed(false);
    reset();
  };
  const onSubmit = handleSubmit(async (data: CreateSellerToken) => {
    try {
      setIsLoading(true);
      const response = await postSellerApiToken(data);
      setCreatedToken(response.data);
      getTokensInitialData();
      showToast({ message: "Token criado com sucesso!", type: "success" });
    } catch (error) {
      error?.response?.data?.errors?.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
      setIsCreateModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (isCreateModalOpen) {
      setValue("title", `Chave ${tokens?.length + 1}`);
      setValue("expiration", "NO_EXPIRATION");
    } else {
      reset();
    }
  }, [isCreateModalOpen, setValue, reset]);
  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
        handleConfirmModal={deleteToken}
        title="Tem certeza que deseja excluir essa chave?"
        isSubmitting={isLoading}
        yesNoText
      >
        <Text fontSize="14px" color="var(--content-primary)">
          Essa ação não poderá ser desfeita.
        </Text>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={isAlertActiveOpen}
        onClose={() => setIsAlertActiveOpen(!isAlertActiveOpen)}
        handleConfirmModal={changeActive}
        isSubmitting={isLoading}
        title={
          chooseToken.active
            ? "Tem certeza que deseja desativar essa chave?"
            : "Tem certeza que deseja reativar essa chave?"
        }
        yesNoText
      >
        {chooseToken.active ? (
          <Text fontSize="14px" color="var(--content-primary)">
            As integrações relativas a essa chave irão parar de funcionar
            imediatamente e não será possível ativá-la novamente.
          </Text>
        ) : (
          <Text fontSize="14px" color="var(--content-primary)">
            As integrações relativas a essa chave começarão a funcionar
            imediatamente.
          </Text>
        )}
      </ConfirmationModal>
      <FormModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title={createdToken ? "Chave criada" : "Nova chave API"}
        handleConfirmModal={createdToken ? closeCreateModal : onSubmit}
        isSubmitting={isLoading}
        disableWhenIsFormEmpty={
          createdToken ? !copyConfirmed : getTitleValue === ""
        }
        confirmationButtonText={createdToken ? "Concluir" : "Salvar"}
      >
        {createdToken ? (
          <Flex direction="column" gap={4}>
            <Text fontSize="14px" color="var(--content-primary)">
              Copie o token agora — ele não será exibido novamente.
            </Text>
            <Flex align="center" gap={2}>
              <Box flex="1">
                <Input name="createdToken" value={createdToken.uuid} readOnly />
              </Box>
              <IconButton
                aria-label="copy to clipboard"
                bgColor="transparent"
                icon={<DuplicateIcon />}
                onClick={() =>
                  copyTextToClipboard(
                    createdToken.uuid,
                    "Token copiado para a área de transferência!"
                  )
                }
                _hover={{
                  cursor: "pointer",
                }}
              />
            </Flex>
            <Checkbox
              label={
                <Text fontSize="14px" color="var(--content-primary)">
                  Confirmo que copiei o token e o guardei em um local seguro
                </Text>
              }
              checked={copyConfirmed}
              onChange={setCopyConfirmed}
            />
          </Flex>
        ) : (
          <Flex direction="column" gap={4} minH="250px">
            <Input
              label="Chave API"
              error={errors?.title?.message as string}
              {...register("title")}
            />
            <Select
              label="Expiração"
              options={expirationOptions}
              value={getExpirationValue ?? "NO_EXPIRATION"}
              onChange={(value) =>
                setValue("expiration", value, { shouldValidate: true })
              }
            />
          </Flex>
        )}
      </FormModal>
      <Flex w="100%" direction="column" gap={4}>
        <Flex align="center">
          <Heading fontSize="20px">Chaves API</Heading>
          <Flex gap={8} ml="auto">
            <Button
              variant="link"
              onClick={() => {
                window.open("https://docs.barte.com/", "_blank");
              }}
            >
              ver documentação
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(!isCreateModalOpen);
              }}
            >
              Nova chave API
            </Button>
          </Flex>
        </Flex>
        {tokens.length > 0 ? (
          <Stack>
            {tokens?.map((token) => (
              <TokenItem key={token.tokenId} token={token} />
            ))}
          </Stack>
        ) : (
          <IntegrationEmptyState message="Nenhuma chave cadastrada ainda" />
        )}
      </Flex>
    </>
  );
};
